import React from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import API from '@aws-amplify/api'
import { useSelector } from 'react-redux'
import { IStockCard } from 'interfaces/IStockCard'
import { IReduxState } from 'interfaces/IReduxState'
import { isUserSuperuser } from 'shared/auth'
import { Department } from 'enums/department'

interface IBatchCandidate {
  productId: number
  batchNumber: string
  productStock: number
  expiryDate: string | null
  productionDate: string | null
}

interface IInvoiceProductWithCandidates {
  invoiceProductId: number
  productId: number
  productName: string
  brandId: string
  packagingId: string
  packagingName: string
  labelId: string
  premixId: string
  notificationNumber: string
  fillingAmount: number | null
  fillingUnit: string
  productQty: number
  unitPrice: number
  volume: number
  box: boolean
  halal: boolean
  wrap: boolean
  seal: boolean
  pictureUrl: string | null
  productInventory: number
  candidates: IBatchCandidate[]
  existingAllocations: Array<{ productId: number; batchNumber: string; qty: number }>
}

const NO_BATCH_KEY = '__NO_BATCH__'

interface ICandidatesResponse {
  stockCard: {
    stockCardId: number
    stockCardDisplayId: string
    invoiceId: number
    invoiceDisplayId: string
    invoiceCreatedAt: string | null
    orderId: number | null
    orderDisplayId: string
    salesOrderId: number | null
    salesOrderDisplayId: string
    notes: string
    inOutDirection: string
    isConfirmed: boolean
    stockCardCreatedAt: string | null
  }
  invoiceProducts: IInvoiceProductWithCandidates[]
}

interface Props {
  stockCardData: IStockCard
  department?: Department
  onConfirmed: () => void
  onError: (msg: string) => void
}

// Per spec: PCS for OT department, KG for everything else (incl. KOSMETIK).
// The unit recorded on the invoice line is ignored — it's frequently wrong.
const fillingUnitFor = (department?: Department) =>
  department === Department.OT ? 'PCS' : 'KG'

const formatDate = (val: string | null | undefined) =>
  val ? new Date(val).toLocaleDateString('id-ID') : '-'

const flagSummary = (item: IInvoiceProductWithCandidates) => {
  const parts: string[] = []
  if (item.box) parts.push('BX')
  if (item.halal) parts.push('HL')
  if (item.seal) parts.push('SR')
  if (item.wrap) parts.push('SG')
  return parts.join('/') || '-'
}

const sanitizeQty = (input: string) => {
  let s = input.replace(/[^0-9.]/g, '')
  const parts = s.split('.')
  if (parts.length > 2) s = parts.shift() + '.' + parts.join('')
  return s
}

export default function InvoiceOutgoingDialog(props: Props) {
  const { stockCardData, department, onConfirmed, onError } = props
  const stockCardId = stockCardData.stockCardId
  const userGroups = useSelector((s: IReduxState) => s.userGroups || [])
  const isSuperuser = isUserSuperuser(userGroups)
  const displayUnit = fillingUnitFor(department)

  const [loading, setLoading] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [data, setData] = React.useState<ICandidatesResponse | null>(null)
  // qty[invoiceProductId][batchNumber] = string
  const [qty, setQty] = React.useState<Record<number, Record<string, string>>>(
    {}
  )

  React.useEffect(() => {
    if (!stockCardId) return
    let cancelled = false
    setLoading(true)
    API.get(
      'products',
      `/invoices/outgoingStockCard/${stockCardId}/candidates`,
      {}
    )
      .then((resp: ICandidatesResponse) => {
        if (cancelled) return
        setData(resp)
        const initial: Record<number, Record<string, string>> = {}
        resp.invoiceProducts.forEach((ip) => {
          const row: Record<string, string> = {}
          for (const alloc of ip.existingAllocations ?? []) {
            const key = `${alloc.productId || ip.productId}_${alloc.batchNumber || NO_BATCH_KEY}`
            row[key] = String(alloc.qty)
          }
          initial[ip.invoiceProductId] = row
        })
        setQty(initial)
      })
      .catch((err) => {
        console.error(err)
        onError('Gagal memuat detail kartu stok.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [stockCardId, onError])

  const setBatchQty = (
    invoiceProductId: number,
    batchNumber: string,
    value: string
  ) => {
    setQty((prev) => ({
      ...prev,
      [invoiceProductId]: {
        ...(prev[invoiceProductId] ?? {}),
        [batchNumber]: sanitizeQty(value),
      },
    }))
  }

  // Hard blockers (Save disabled). Stock caps are enforced for all users
  // except superuser, who can drive stock negative on purpose.
  const validation = React.useMemo(() => {
    if (!data) return null
    for (const ip of data.invoiceProducts) {
      const allocs = qty[ip.invoiceProductId] ?? {}
      let sum = 0
      for (const [key, raw] of Object.entries(allocs)) {
        const n = Number(raw)
        if (raw === '' || !Number.isFinite(n) || n < 0) continue
        sum += n
        if (key.endsWith(NO_BATCH_KEY)) {
          if (!isSuperuser) {
            return `Pengeluaran tanpa No. Bets hanya boleh dilakukan superuser.`
          }
        } else {
          const [pidStr, ...batchParts] = key.split('_')
          const pid = Number(pidStr)
          const batchNumber = batchParts.join('_')
          const candidate = ip.candidates.find((c) => c.productId === pid && c.batchNumber === batchNumber)
          if (candidate && !isSuperuser && n > candidate.productStock) {
            return `Qty Dikeluarkan untuk batch ${batchNumber} (${n}) melebihi stok (${candidate.productStock}).`
          }
        }
      }
      if (!isSuperuser && Math.abs(sum - ip.productQty) > 1e-9) {
        return `Total Qty Dikeluarkan untuk ${ip.productName} (${sum}) harus sama dengan Qty di invoice (${ip.productQty}).`
      }
    }
    return null
  }, [data, qty, isSuperuser])

  // Soft warning when superuser is about to push stock negative.
  const negativeStockWarning = React.useMemo(() => {
    if (!data || !isSuperuser) return null
    for (const ip of data.invoiceProducts) {
      const allocs = qty[ip.invoiceProductId] ?? {}
      for (const [key, raw] of Object.entries(allocs)) {
        const n = Number(raw)
        if (raw === '' || !Number.isFinite(n) || n <= 0) continue
        if (key.endsWith(NO_BATCH_KEY)) {
          if (n > ip.productInventory) {
            return `Qty tanpa No. Bets untuk ${ip.productName} (${n}) melebihi stok tblProduct (${ip.productInventory}). Stok akan menjadi negatif.`
          }
        } else {
          const [pidStr, ...batchParts] = key.split('_')
          const pid = Number(pidStr)
          const batchNumber = batchParts.join('_')
          const candidate = ip.candidates.find((c) => c.productId === pid && c.batchNumber === batchNumber)
          if (candidate && n > candidate.productStock) {
            return `Qty batch ${batchNumber} (${n}) melebihi stok (${candidate.productStock}). Stok batch akan menjadi negatif.`
          }
        }
      }
    }
    return null
  }, [data, qty, isSuperuser])

  const handleSave = async () => {
    if (!data || !stockCardId) return
    const allocations: Array<{
      invoiceProductId: number
      productId: number
      batchNumber: string
      qtyDikeluarkan: number
    }> = []
    for (const ip of data.invoiceProducts) {
      const allocs = qty[ip.invoiceProductId] ?? {}
      for (const [key, raw] of Object.entries(allocs)) {
        const n = Number(raw)
        if (raw === '' || !Number.isFinite(n) || n <= 0) continue
        if (key.endsWith(NO_BATCH_KEY)) {
          const [pidStr] = key.split('_')
          allocations.push({
            invoiceProductId: ip.invoiceProductId,
            productId: Number(pidStr) || ip.productId,
            batchNumber: '',
            qtyDikeluarkan: n,
          })
        } else {
          const [pidStr, ...batchParts] = key.split('_')
          const pid = Number(pidStr)
          const batchNumber = batchParts.join('_')
          allocations.push({
            invoiceProductId: ip.invoiceProductId,
            productId: pid,
            batchNumber: batchNumber,
            qtyDikeluarkan: n,
          })
        }
      }
    }
    if (allocations.length === 0) {
      onError('Belum ada Qty Dikeluarkan yang diisi.')
      return
    }
    setSaving(true)
    try {
      await API.post(
        'products',
        `/invoices/outgoingStockCard/${stockCardId}/confirmAllocations`,
        { body: { allocations } }
      )
      onConfirmed()
    } catch (err: any) {
      console.error(err)
      const detail =
        err?.response?.data?.message ||
        err?.message ||
        'Gagal menyimpan konfirmasi.'
      onError(String(detail))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box p={3} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    )
  }
  if (!data) {
    return (
      <Box p={3}>
        <Alert severity="error">Data tidak tersedia.</Alert>
      </Box>
    )
  }

  const isConfirmed = !!data.stockCard.isConfirmed

  return (
    <Box p={2}>
      <Box mb={2}>
        <Typography variant="h6" gutterBottom>
          Detail Kartu Stok Keluar Gudang Jadi
        </Typography>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell sx={{ width: '30%', fontWeight: 'bold' }}>
                No. Dokumen
              </TableCell>
              <TableCell>{data.stockCard.stockCardDisplayId || '-'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Masuk/Keluar</TableCell>
              <TableCell>Keluar</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Order ID</TableCell>
              <TableCell>{data.stockCard.orderDisplayId || '-'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>
                Sales Order ID (optional)
              </TableCell>
              <TableCell>
                {data.stockCard.salesOrderDisplayId || '-'}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Invoice ID</TableCell>
              <TableCell>{data.stockCard.invoiceDisplayId || '-'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>
                Tanggal Invoice Dibuat
              </TableCell>
              <TableCell>{formatDate(data.stockCard.invoiceCreatedAt)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Notes</TableCell>
              <TableCell>{data.stockCard.notes || '-'}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>

      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 3 }}>
        Daftar Produk
      </Typography>
      <TableContainer component={Paper} sx={{ mt: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Item Number</TableCell>
              <TableCell>Qty</TableCell>
              <TableCell>ID Produk</TableCell>
              <TableCell>Nama</TableCell>
              <TableCell>Kemasan</TableCell>
              <TableCell>Filling</TableCell>
              <TableCell>Volume</TableCell>
              <TableCell>BX HL SR SG</TableCell>
              <TableCell>Gambar Kemasan</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.invoiceProducts.map((ip, idx) => (
              <TableRow key={ip.invoiceProductId}>
                <TableCell>{idx + 1}</TableCell>
                <TableCell>{ip.productQty}</TableCell>
                <TableCell>{ip.productId}</TableCell>
                <TableCell>{ip.productName}</TableCell>
                <TableCell>{ip.packagingId}</TableCell>
                <TableCell>
                  {ip.fillingAmount != null
                    ? `${ip.fillingAmount}${displayUnit}`
                    : '-'}
                </TableCell>
                <TableCell>{ip.volume || '-'}</TableCell>
                <TableCell>{flagSummary(ip)}</TableCell>
                <TableCell>
                  {ip.pictureUrl ? (
                    <img
                      src={ip.pictureUrl}
                      alt="Gambar Kemasan"
                      style={{
                        maxWidth: 60,
                        maxHeight: 60,
                        objectFit: 'contain',
                      }}
                    />
                  ) : (
                    '-'
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 3 }}>
        Daftar Barang Jadi Dikeluarkan
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Menampilkan batch yang belum kedaluwarsa dengan stok &gt; 0.
      </Typography>
      <List>
        {data.invoiceProducts.map((ip, idx) => (
          <ListItem key={ip.invoiceProductId} sx={{ display: 'block' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Item Nomor {idx + 1}: {ip.productName} (Qty invoice:{' '}
              {ip.productQty})
            </Typography>
            {(() => {
              if (!isSuperuser && ip.candidates.length === 0) {
                return (
                  <Alert severity="warning">
                    Tidak ada batch yang tersedia (semua kedaluwarsa atau stok
                    0). Hubungi superuser untuk konfirmasi tanpa No. Bets.
                  </Alert>
                )
              }
              return (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          Qty Dikeluarkan
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>ID Produk</TableCell>
                        <TableCell>Nama</TableCell>
                        <TableCell>Merek</TableCell>
                        <TableCell>Kode Kemasan</TableCell>
                        <TableCell>Label</TableCell>
                        <TableCell>Kode Ruahan</TableCell>
                        <TableCell>Filling Amount</TableCell>
                        <TableCell>Halal/Dus/SW/Segel</TableCell>
                        <TableCell>No Bets</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>STOK</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ip.candidates.map((cand) => {
                        const candKey = `${cand.productId}_${cand.batchNumber}`
                        return (
                        <TableRow key={candKey}>
                          <TableCell>
                            <TextField
                              size="small"
                              value={
                                qty[ip.invoiceProductId]?.[candKey] ??
                                ''
                              }
                              onChange={(e) =>
                                setBatchQty(
                                  ip.invoiceProductId,
                                  candKey,
                                  e.target.value
                                )
                              }
                              disabled={isConfirmed || saving}
                              inputProps={{
                                inputMode: 'decimal',
                                style: { width: 80 },
                              }}
                            />
                          </TableCell>
                          <TableCell>{cand.productId}</TableCell>
                          <TableCell>{ip.productName}</TableCell>
                          <TableCell>{ip.brandId}</TableCell>
                          <TableCell>{ip.packagingId}</TableCell>
                          <TableCell>{ip.labelId || '-'}</TableCell>
                          <TableCell>{ip.premixId || '-'}</TableCell>
                          <TableCell>
                            {ip.fillingAmount != null
                              ? `${ip.fillingAmount}${displayUnit}`
                              : '-'}
                          </TableCell>
                          <TableCell>{flagSummary(ip)}</TableCell>
                          <TableCell>{cand.batchNumber}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            {cand.productStock}
                          </TableCell>
                        </TableRow>
                        )
                      })}
                      {(() => {
                        const hasSavedNoBatch = (
                          ip.existingAllocations ?? []
                        ).some((a) => !a.batchNumber)
                        const showRow =
                          hasSavedNoBatch ||
                          (isSuperuser && ip.candidates.length === 0)
                        return showRow
                      })() && (
                        <TableRow sx={{ backgroundColor: '#fff8e1' }}>
                          <TableCell>
                            <TextField
                              size="small"
                              value={
                                qty[ip.invoiceProductId]?.[`${ip.productId}_${NO_BATCH_KEY}`] ?? ''
                              }
                              onChange={(e) =>
                                setBatchQty(
                                  ip.invoiceProductId,
                                  `${ip.productId}_${NO_BATCH_KEY}`,
                                  e.target.value
                                )
                              }
                              disabled={isConfirmed || saving}
                              inputProps={{
                                inputMode: 'decimal',
                                style: { width: 80 },
                              }}
                            />
                          </TableCell>
                          <TableCell>{ip.productId}</TableCell>
                          <TableCell>{ip.productName}</TableCell>
                          <TableCell>{ip.brandId}</TableCell>
                          <TableCell>{ip.packagingId}</TableCell>
                          <TableCell>{ip.labelId || '-'}</TableCell>
                          <TableCell>{ip.premixId || '-'}</TableCell>
                          <TableCell>
                            {ip.fillingAmount != null
                              ? `${ip.fillingAmount}${displayUnit}`
                              : '-'}
                          </TableCell>
                          <TableCell>{flagSummary(ip)}</TableCell>
                          <TableCell
                            sx={{ fontStyle: 'italic', color: '#a35a00' }}
                          >
                            (Tanpa No. Bets — superuser)
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            {ip.productInventory}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )
            })()}
          </ListItem>
        ))}
      </List>

      {validation && !isConfirmed && (
        <Box mt={2}>
          <Alert severity="warning">{validation}</Alert>
        </Box>
      )}
      {negativeStockWarning && !isConfirmed && (
        <Box mt={2}>
          <Alert severity="info">{negativeStockWarning}</Alert>
        </Box>
      )}

      {!isConfirmed && (
        <Box mt={3} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving || !!validation}
            startIcon={
              saving ? <CircularProgress size={18} color="inherit" /> : null
            }
          >
            Save
          </Button>
        </Box>
      )}
      {isConfirmed && (
        <Box mt={3}>
          <Alert severity="info">Kartu stok ini sudah dikonfirmasi.</Alert>
        </Box>
      )}
    </Box>
  )
}
